import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import PageHeader from "../components/PageHeader";
import { MANUAL, MANUAL_INDEX, MANUAL_UPDATED, ROLE_LABEL } from "../content/manual";
import { Search, Printer, ChevronDown, ChevronRight, Info, AlertTriangle, X } from "lucide-react";

// *stars* mark bold in the manual content.
const Rich = ({ text }) => (
  <>
    {String(text).split("*").map((chunk, i) =>
      i % 2 ? <strong key={i} className="text-[var(--ink)] font-semibold">{chunk}</strong> : <span key={i}>{chunk}</span>
    )}
  </>
);

const Block = ({ block }) => {
  if (block.p) return <p className="t-body leading-relaxed"><Rich text={block.p} /></p>;
  if (block.list)
    return (
      <ul className="space-y-1.5">
        {block.list.map((li, i) => (
          <li key={i} className="t-body leading-relaxed pl-4 relative">
            <span className="absolute left-0 top-[0.6em] w-1.5 h-px bg-[var(--rule-strong)]" />
            <Rich text={li} />
          </li>
        ))}
      </ul>
    );
  if (block.steps)
    return (
      <ol className="space-y-2">
        {block.steps.map((li, i) => (
          <li key={i} className="t-body leading-relaxed grid grid-cols-[1.6rem_1fr] gap-1">
            <span className="font-mono-num text-xs text-[var(--soft-fg)] pt-0.5">{i + 1}.</span>
            <span><Rich text={li} /></span>
          </li>
        ))}
      </ol>
    );
  if (block.defs)
    return (
      <dl className="border-t border-[var(--rule)]">
        {block.defs.map(([term, def], i) => (
          <div key={i} className="grid sm:grid-cols-[minmax(9rem,13rem)_1fr] gap-x-5 gap-y-1 py-2.5 border-b border-[var(--rule)]">
            <dt className="eyebrow text-[var(--ink)] normal-case tracking-[0.05em]">{term}</dt>
            <dd className="t-body leading-relaxed"><Rich text={def} /></dd>
          </div>
        ))}
      </dl>
    );
  if (block.table)
    return (
      <div className="overflow-x-auto thin-scroll border border-[var(--rule)]">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr>
              {block.table.head.map((h, i) => (
                <th key={i} className="eyebrow text-left px-3 py-2 border-b border-[var(--rule-strong)] bg-[var(--paper-soft)] whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.table.rows.map((row, r) => (
              <tr key={r} className="border-b border-[var(--rule)] last:border-b-0">
                {row.map((cell, c) => (
                  <td key={c} className={`px-3 py-2 align-top ${c === 0 ? "text-[var(--ink)]" : "text-[var(--muted-fg)]"}`}>
                    <Rich text={cell} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  if (block.note || block.warn) {
    const warn = !!block.warn;
    const Icon = warn ? AlertTriangle : Info;
    return (
      <div className={`flex items-start gap-3 p-4 border-l-2 ${warn ? "border-[#B45309] bg-[#FBF7EF]" : "border-[var(--rule-strong)] bg-[var(--paper-soft)]"}`}>
        <Icon size={14} className={`mt-0.5 shrink-0 ${warn ? "text-[#B45309]" : "text-[var(--muted-fg)]"}`} />
        <p className="t-body leading-relaxed"><Rich text={block.warn || block.note} /></p>
      </div>
    );
  }
  return null;
};

const Topic = ({ topic }) => (
  <article id={topic.id} className="scroll-mt-28 py-6 border-t border-[var(--rule)] first:border-t-0" data-testid={`manual-topic-${topic.id}`}>
    <h3 className="font-display tracked-tight text-base md:text-lg text-[var(--ink)]">{topic.title}</h3>
    <div className="mt-3 space-y-4 max-w-3xl">
      {topic.blocks.map((b, i) => <Block key={i} block={b} />)}
    </div>
  </article>
);

export default function Manual() {
  const { user } = useAuth();
  const { hash } = useLocation();
  const [query, setQuery] = useState("");
  const [scope, setScope] = useState("mine");
  const [open, setOpen] = useState(() => ({ start: true }));

  const q = query.trim().toLowerCase();
  const matches = useMemo(() => {
    if (!q) return null;
    return new Set(MANUAL_INDEX.filter((r) => r.haystack.includes(q)).map((r) => `${r.sectionId}·${r.topicId}`));
  }, [q]);

  const visible = useMemo(() => {
    const allowed = (roles) => !roles || scope === "all" || roles.includes(user?.role);
    return MANUAL.map((section) => {
      if (!allowed(section.roles)) return null;
      const topics = section.topics.filter(
        (t) => allowed(t.roles) && (!matches || matches.has(`${section.id}·${t.id}`))
      );
      return topics.length ? { ...section, topics } : null;
    }).filter(Boolean);
  }, [matches, scope, user]);

  const hitCount = visible.reduce((n, s) => n + s.topics.length, 0);
  const isOpen = (id) => (q ? true : !!open[id]);

  // Deep links: /help#pf-invoicing opens that section and scrolls to the topic.
  useEffect(() => {
    if (!hash) return;
    const target = hash.slice(1);
    const section = MANUAL.find((s) => s.topics.some((t) => t.id === target) || s.id === target);
    if (!section) return;
    // Only widen the scope when the target would otherwise be filtered out.
    const topic = section.topics.find((t) => t.id === target);
    const allowed = (roles) => !roles || roles.includes(user?.role);
    if (!allowed(section.roles) || (topic && !allowed(topic.roles))) setScope("all");
    setOpen((o) => ({ ...o, [section.id]: true }));
    setTimeout(() => document.getElementById(target)?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
  }, [hash, user]);

  const setAll = (value) =>
    setOpen(MANUAL.reduce((acc, s) => ({ ...acc, [s.id]: value }), {}));

  const print = () => {
    setAll(true);
    setTimeout(() => window.print(), 200);
  };

  return (
    <div className="space-y-8" data-testid="manual-view">
      <PageHeader
        section="HELP"
        title="How this works"
        subtitle="A plain-English guide to every screen, every figure and the handful of rules that lock things down. Search it, or skim the section you need."
        right={
          <button
            type="button"
            onClick={print}
            title="Print or save as PDF (Cmd/Ctrl-P)"
            className="no-print inline-flex items-center gap-2 px-3 py-2 border border-[var(--rule-strong)] hover:bg-[var(--paper-soft)] font-display text-[11px] tracked-tight uppercase text-[var(--muted-fg)]"
            data-testid="manual-print"
          >
            <Printer size={13} /> Print manual
          </button>
        }
      />

      <div className="no-print flex flex-wrap items-center gap-3 pb-1">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--soft-fg)]" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search the manual — try 'invoicing', 'leave', 'locked'"
            className="w-full border border-[var(--rule-strong)] pl-9 pr-8 py-2 text-sm focus:outline-none focus:border-[var(--ink)]"
            data-testid="manual-search"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-[var(--paper-soft)]"
              aria-label="Clear search"
              data-testid="manual-search-clear"
            >
              <X size={12} />
            </button>
          )}
        </div>
        <div className="inline-flex border border-[var(--rule-strong)]" data-testid="manual-scope-toggle">
          {[
            { id: "mine", label: user?.role ? `${ROLE_LABEL[user.role] || "My"} view` : "My view" },
            { id: "all", label: "Everything" },
          ].map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setScope(s.id)}
              className={`px-3 py-1.5 text-[11px] font-display tracked-tight uppercase ${scope === s.id ? "bg-[var(--ink)] text-white" : "text-[var(--muted-fg)] hover:bg-[var(--paper-soft)]"}`}
              data-testid={`manual-scope-${s.id}`}
            >
              {s.label}
            </button>
          ))}
        </div>
        {!q && (
          <div className="inline-flex border border-[var(--rule-strong)]">
            <button type="button" onClick={() => setAll(true)} className="px-3 py-1.5 text-[11px] font-display tracked-tight uppercase text-[var(--muted-fg)] hover:bg-[var(--paper-soft)]" data-testid="manual-expand-all">Expand all</button>
            <button type="button" onClick={() => setAll(false)} className="px-3 py-1.5 text-[11px] font-display tracked-tight uppercase border-l border-[var(--rule-strong)] text-[var(--muted-fg)] hover:bg-[var(--paper-soft)]" data-testid="manual-collapse-all">Collapse all</button>
          </div>
        )}
        <span className="eyebrow font-mono-num ml-auto" data-testid="manual-meta">
          {q ? `${hitCount} ${hitCount === 1 ? "topic" : "topics"} match` : `updated ${MANUAL_UPDATED}`}
        </span>
      </div>

      <div className="grid lg:grid-cols-[220px_1fr] gap-8 lg:gap-12 items-start">
        <nav className="no-print hidden lg:block sticky top-6 space-y-1" data-testid="manual-toc">
          {MANUAL.map((s) => {
            const shown = visible.find((v) => v.id === s.id);
            return (
              <a
                key={s.id}
                href={`#${s.id}`}
                onClick={() => setOpen((o) => ({ ...o, [s.id]: true }))}
                className={`block py-1.5 text-xs font-display tracked-tight uppercase transition-colors ${shown ? "text-[var(--muted-fg)] hover:text-[var(--ink)]" : "text-[var(--rule-strong)] pointer-events-none"}`}
                data-testid={`manual-toc-${s.id}`}
              >
                {s.title}
              </a>
            );
          })}
        </nav>

        <div className="space-y-4 min-w-0">
          {visible.length === 0 && (
            <div className="border border-[var(--rule)] p-8 text-center" data-testid="manual-empty">
              <div className="eyebrow text-[var(--soft-fg)]">Nothing matches “{query}”</div>
              <p className="t-body mt-2">Try a plainer word — “invoice”, “leave”, “locked”, “approve” — or switch to Everything.</p>
            </div>
          )}
          {visible.map((section) => (
            <section key={section.id} id={section.id} className="scroll-mt-24 border border-[var(--rule)]" data-testid={`manual-section-${section.id}`}>
              <button
                type="button"
                onClick={() => setOpen((o) => ({ ...o, [section.id]: !o[section.id] }))}
                className="w-full text-left px-5 py-4 flex items-start gap-3 hover:bg-[var(--paper-soft)] transition-colors"
                data-testid={`manual-section-toggle-${section.id}`}
              >
                <span className="mt-0.5 text-[var(--muted-fg)]">
                  {isOpen(section.id) ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block font-display tracked-tight text-base md:text-lg text-[var(--ink)]">{section.title}</span>
                  <span className="block t-caption mt-0.5">{section.blurb}</span>
                </span>
                <span className="eyebrow font-mono-num text-[var(--soft-fg)] shrink-0">{section.topics.length}</span>
              </button>
              <div className={`px-5 pb-2 ${isOpen(section.id) ? "" : "hidden print:block"}`} data-testid={`manual-body-${section.id}`}>
                {section.topics.map((t) => <Topic key={t.id} topic={t} />)}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
